// packages/web/src/components/KnotConfirmDialog.tsx — ported 2026-07-29
// from the Rexx fork's 2026-07-21 build: the "never assume" confirm
// surface. Every voice-driven create whose parent isn't EXACTLY the record
// the Knot was held from stops here rather than guessing. Three ways out:
// confirm the suggested match, search for a different one, or create the
// parent fresh. Fully generic already — only consumes the generic
// KnotCandidate/KnotContextType shapes, no HVAC-specific rendering.
import { useEffect, useState } from 'preact/hooks';
import { listAllRecordsByType, type KnotCandidate, type KnotContextType, type RecordRow } from '../lib/api';
import { openRecordEditor } from '../lib/recordEditor';
import './KnotConfirmDialog.css';

export interface KnotConfirmDialogProps {
  tenantId: string;
  targetLabel: string;
  /** A short human name for the thing being created, computed server-side
   * (Item 7's route, from the target's declared nameField) — purely
   * descriptive in the prompt. */
  itemName: string;
  parentType: KnotContextType;
  parentLabel: string;
  candidates: KnotCandidate[];
  suggestion: KnotCandidate | null;
  onConfirm: (parent: KnotCandidate) => void;
  onCreateNewParent: () => void;
  onCancel: () => void;
}

export function KnotConfirmDialog({
  tenantId,
  targetLabel,
  itemName,
  parentType,
  parentLabel,
  candidates,
  suggestion,
  onConfirm,
  onCreateNewParent,
  onCancel,
}: KnotConfirmDialogProps) {
  const [searching, setSearching] = useState(suggestion === null && candidates.length === 0);
  const [query, setQuery] = useState('');
  const [allOptions, setAllOptions] = useState<RecordRow[] | null>(null);

  useEffect(() => {
    if (!searching) return;
    // v1.1.2 — ALL records of the type, not the API's default first page:
    // at 1,423 clients the old single-page load searched the 50 OLDEST rows,
    // so "Wil" answered "No match." while Wiley Coyote sat visibly in the
    // list behind the dialog. listAll pages in parallel waves (R-9), so the
    // one-time load is a few round trips, cached for the dialog's lifetime.
    listAllRecordsByType(tenantId, parentType)
      .then(setAllOptions)
      .catch(() => setAllOptions([]));
  }, [searching, tenantId, parentType]);

  const filtered =
    allOptions?.filter((r) => r.display_name.toLowerCase().includes(query.trim().toLowerCase())).slice(0, 30) ?? [];

  return (
    <div class="knot-confirm__scrim" onClick={onCancel}>
      <section class="knot-confirm" onClick={(e) => e.stopPropagation()}>
        <p class="knot-confirm__what">
          {targetLabel}
          {itemName ? ` — ${itemName}` : ''}
        </p>

        {!searching && suggestion && (
          <>
            <p class="knot-confirm__question">For {suggestion.name}?</p>
            <div class="knot-confirm__row">
              <button type="button" class="knot-confirm__yes" onClick={() => onConfirm(suggestion)}>
                Yes
              </button>
              <button type="button" class="knot-confirm__alt" onClick={() => setSearching(true)}>
                Not this one
              </button>
            </div>
          </>
        )}

        {!searching && !suggestion && candidates.length > 0 && (
          <>
            <p class="knot-confirm__question">Which {parentLabel.toLowerCase()}?</p>
            <ul class="knot-confirm__list">
              {candidates.map((c) => (
                <li key={c.id}>
                  <button type="button" class="knot-confirm__option" onClick={() => onConfirm(c)}>
                    {c.name}
                  </button>
                </li>
              ))}
            </ul>
            <button type="button" class="knot-confirm__alt" onClick={() => setSearching(true)}>
              Search instead
            </button>
          </>
        )}

        {searching && (
          <>
            <p class="knot-confirm__question">Which {parentLabel.toLowerCase()}?</p>
            <input
              class="knot-confirm__search"
              placeholder={`Search ${parentLabel.toLowerCase()}s…`}
              value={query}
              onInput={(e) => setQuery((e.target as HTMLInputElement).value)}
              autoFocus
            />
            {allOptions === null ? (
              <p class="knot-confirm__soft">Loading…</p>
            ) : filtered.length === 0 ? (
              <p class="knot-confirm__soft">No match.</p>
            ) : (
              <ul class="knot-confirm__list">
                {filtered.map((r) => (
                  <li key={r.id}>
                    <button
                      type="button"
                      class="knot-confirm__option"
                      onClick={() => onConfirm({ id: r.id, name: r.display_name })}
                    >
                      {r.display_name}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </>
        )}

        <div class="knot-confirm__footer">
          <button
            type="button"
            class="knot-confirm__new"
            onClick={() => {
              openRecordEditor({ mode: 'create', typeKey: parentType });
              onCreateNewParent();
            }}
          >
            + New {parentLabel}
          </button>
          <button type="button" class="knot-confirm__cancel" onClick={onCancel}>
            Cancel
          </button>
        </div>
      </section>
    </div>
  );
}
